import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { azure, black, screenSmMin, screenMdMin } from '@spotify-internal/encore-foundation';
import { useLocalePreferenceContext } from '../../hooks/useLocalePreference';

export function Video() {
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const localePreference = useLocalePreferenceContext();

  return (
    <VideoContainer>
      <div style={{ display: 'inline-block', position: 'relative', lineHeight: 1 }}>
        <VideoDecoration />
        {showVideoPlayer ? (
          <VideoIframe
            data-testid="landing-page-old--video-player"
            src={`${localePreference.brandVideoUrl}?autoplay=1&iv_load_policy=3&playsinline=1&rel=0&modestbranding=1`}
            frameBorder="0"
            allowFullScreen
            allow="autoplay; encrypted-media"
          />
        ) : (
          <VideoThumbnail
            data-testid="landing-page-old--video-thumbnail"
            onClick={() => {
              setShowVideoPlayer(true);
            }}
          >
            <VideoImg src="/images/landing_page/old/video-thumbnail.png" alt="" />
          </VideoThumbnail>
        )}
      </div>
    </VideoContainer>
  );
}

const videoHorizontalPadding = 16;
const zagWidth = '43px';
const zagHeight = '124px';

const VideoContainer = styled.div`
  background: ${azure};
  text-align: center;
  padding: 70px ${videoHorizontalPadding}px;

  @media (min-width: ${screenMdMin}) {
    background: transparent;
    padding: 90px 0 76px;
  }
`;

const VideoDecoration = styled.div`
  display: none;

  @media (min-width: ${screenMdMin}) {
    background-image: url('/images/landing_page/old/vertical-sawtooth.png');
    background-size: ${zagWidth} ${zagHeight};
    display: block;
    width: ${zagWidth};
    height: ${zagHeight};
    position: absolute;
    top: calc(100% - ${zagHeight} - 80px);
    left: calc(-${zagWidth} - 28px);
  }

  @media (min-width: ${screenMdMin}) and (min-resolution: 192dpi) {
    background-image: url('/images/landing_page/old/vertical-sawtooth@2x.png');
  }
`;

const videoWidth = 984;
const videoHeight = 554;
const videoDimensions = css`
  /* preserve the 16:9 aspect ratio */
  width: calc(100vw - ${videoHorizontalPadding * 2}px);
  height: calc((100vw - ${videoHorizontalPadding * 2}px) * ${videoHeight} / ${videoWidth});
  min-width: 343px;
  min-height: 193px;

  @media (min-width: ${screenMdMin}) {
    width: ${videoWidth}px;
    height: ${videoHeight}px;
  }
`;

const VideoIframe = styled.iframe`
  position: relative;
  margin: auto;
  display: inline-block;
  background-color: ${black};

  ${videoDimensions}
`;

const VideoThumbnail = styled.div`
  display: inline-block;
  position: relative;
  cursor: pointer;

  &::before {
    background-image: url('data:image/svg+xml;charset=utf-8,%3Csvg%20width%3D%2272px%22%20height%3D%2272px%22%20viewBox%3D%220%200%2072%2072%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%0D%0A%20%20%20%20%3C%21--%20Generator%3A%20Sketch%2049.2%20%2851160%29%20-%20http%3A%2F%2Fwww.bohemiancoding.com%2Fsketch%20--%3E%0D%0A%20%20%20%20%3Cdesc%3ECreated%20with%20Sketch.%3C%2Fdesc%3E%0D%0A%20%20%20%20%3Cdefs%3E%0D%0A%20%20%20%20%20%20%20%20%3Ccircle%20id%3D%22path-1%22%20cx%3D%2235.6%22%20cy%3D%2235.6%22%20r%3D%2235.6%22%3E%3C%2Fcircle%3E%0D%0A%20%20%20%20%3C%2Fdefs%3E%0D%0A%20%20%20%20%3Cg%20id%3D%22Video-Pages%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%0D%0A%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Artboard-2%22%20transform%3D%22translate%28-932.000000%2C%20-651.000000%29%22%3E%0D%0A%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22VideoHomeThumbnails-Copy-7%22%20transform%3D%22translate%28767.000000%2C%20574.000000%29%22%3E%0D%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Group-3%22%3E%0D%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22PlayButton%22%20transform%3D%22translate%28165.000000%2C%2077.000000%29%22%3E%0D%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Oval%22%3E%0D%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cuse%20fill-opacity%3D%220.5%22%20fill%3D%22%23000000%22%20fill-rule%3D%22evenodd%22%20xlink%3Ahref%3D%22%23path-1%22%3E%3C%2Fuse%3E%0D%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Ccircle%20stroke%3D%22%23FFFFFF%22%20stroke-width%3D%222.8%22%20cx%3D%2235.6%22%20cy%3D%2235.6%22%20r%3D%2234.2%22%3E%3C%2Fcircle%3E%0D%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%0D%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpolygon%20id%3D%22Shape%22%20fill%3D%22%23FFFFFF%22%20fill-rule%3D%22nonzero%22%20points%3D%2250.7515152%2035.3703704%2025.6%2020%2025.6%2050.7407407%22%3E%3C%2Fpolygon%3E%0D%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%0D%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%0D%0A%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%0D%0A%20%20%20%20%20%20%20%20%3C%2Fg%3E%0D%0A%20%20%20%20%3C%2Fg%3E%0D%0A%3C%2Fsvg%3E');
    background-size: cover;
    content: '';
    display: block;
    height: 57px;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    transition: opacity 0.3s;
    width: 57px;
    z-index: 1;
    pointer-events: none;
  }

  @media (min-width: ${screenSmMin}) {
    &::before {
      height: 71px;
      opacity: 0;
      width: 71px;
    }

    &:hover::before {
      opacity: 1;
    }
  }
`;

const VideoImg = styled.img`
  ${videoDimensions}
`;
